import React, { useMemo } from 'react';
import { Card, Title } from '@mantine/core';
import {
  RadarChart as RechartsRadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { CompetencyScore } from '../../types';

interface RadarChartProps {
  scores: CompetencyScore[];
}

interface RadarDataPoint {
  subject: string;
  self: number | null;
  manager: number | null;
  peers: number | null;
  subordinates: number | null;
}

const LINES = [
  { key: 'self', color: '#228be6', labelKey: 'review360.roles.self' },
  { key: 'manager', color: '#9c36b5', labelKey: 'review360.roles.manager' },
  { key: 'peers', color: '#12b886', labelKey: 'review360.roles.peer' },
  { key: 'subordinates', color: '#fd7e14', labelKey: 'review360.roles.subordinate' },
] as const;

export const RadarChart: React.FC<RadarChartProps> = ({ scores }) => {
  const { t } = useTranslation();

  const data: RadarDataPoint[] = useMemo(
    () =>
      scores.map((s) => ({
        subject: s.sectionTitle,
        self: s.selfScore,
        manager: s.managerScore,
        peers: s.peerScoreAvg,
        subordinates: s.subordinateScoreAvg,
      })),
    [scores],
  );

  // Determine which lines have at least one non-null value
  const visibleLines = useMemo(
    () => LINES.filter((line) => data.some((d) => d[line.key as keyof RadarDataPoint] !== null)),
    [data],
  );

  if (data.length === 0) return null;

  return (
    <Card withBorder radius="md" p="lg">
      <Title order={4} mb="md">
        {t('review360.competencyOverview')}
      </Title>
      <ResponsiveContainer width="100%" height={400}>
        <RechartsRadarChart data={data}>
          <PolarGrid />
          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
          <PolarRadiusAxis domain={[0, 5]} tickCount={6} />
          {visibleLines.map((line) => (
            <Radar
              key={line.key}
              name={t(line.labelKey)}
              dataKey={line.key}
              stroke={line.color}
              fill={line.color}
              fillOpacity={0.1}
              strokeWidth={2}
              connectNulls
            />
          ))}
          <Tooltip />
          <Legend />
        </RechartsRadarChart>
      </ResponsiveContainer>
    </Card>
  );
};
